// hooks/useUsageLast7DaysVanilla.js
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { getFacilityMetricsSummary } from '../services/apiServices';

// ---------- helpers locales (sin tocar utils) ----------

// normaliza: 'YYYY-MM-DD' | Date -> Date(UTC 00:00)
const asUtcMidnight = (input) => {
  if (input instanceof Date) {
    return new Date(Date.UTC(input.getFullYear(), input.getMonth(), input.getDate()));
  }
  const [y, m, d] = String(input || '')
    .slice(0, 10)
    .split('-')
    .map(Number);
  return new Date(Date.UTC(y, (m || 1) - 1, d || 1));
};

const toISODateUTC = (dt) => {
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// lunes (weekStartsOn=1) de la semana que contiene 'date'
const startOfWeekUTC = (date, weekStartsOn = 0) => {
  const d = asUtcMidnight(date);
  const dow = d.getUTCDay(); // 0..6 (Dom..Sáb)
  const shift = (dow - weekStartsOn + 7) % 7;
  const start = new Date(d);
  start.setUTCDate(d.getUTCDate() - shift);
  return start;
};

const endOfWeekUTC = (date, weekStartsOn = 0) => {
  const s = startOfWeekUTC(date, weekStartsOn);
  const e = new Date(s);
  e.setUTCDate(s.getUTCDate() + 6);
  return e;
};

// 7 días desde un lunes dado (YYYY-MM-DD)
const getSevenDaysFromMonday = (mondayISO) => {
  const base = asUtcMidnight(mondayISO);
  return Array.from({ length: 7 }, (_, i) => {
    const dt = new Date(base);
    dt.setUTCDate(base.getUTCDate() + i);
    return {
      label: dt.toLocaleDateString('en-US', { weekday: 'short', day: '2-digit' }).toUpperCase(),
      value: toISODateUTC(dt),
    };
  });
};

// semanas que pisan el mes de 'anchorISO'. value = lunes ISO
const getWeeksOfMonthFromAnchor = (anchorISO, weekStartsOn = 0) => {
  const anchor = asUtcMidnight(anchorISO);
  const monthStart = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), 1));
  const monthEnd = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() + 1, 0));

  let cursor = startOfWeekUTC(monthStart, weekStartsOn);
  const out = [];

  while (cursor <= monthEnd) {
    const ws = new Date(cursor);
    const we = endOfWeekUTC(ws, weekStartsOn);

    // número de semana simple (no ISO estricto)
    const yearStart = new Date(Date.UTC(ws.getUTCFullYear(), 0, 1));
    const daysFromYearStart = Math.floor((ws - yearStart) / (24 * 60 * 60 * 1000));
    const weekNumber = Math.max(
      1,
      Math.ceil((daysFromYearStart + (yearStart.getUTCDay() + 1)) / 7),
    );

    const fmt = (dt, withYear = false) => {
      const s = dt.toLocaleString('en-US', { month: 'short', day: 'numeric' }).toUpperCase();
      return withYear ? `${s}, ${dt.getUTCFullYear()}` : s;
    };

    let subtitle;
    if (ws.getUTCFullYear() === we.getUTCFullYear()) {
      if (ws.getUTCMonth() === we.getUTCMonth()) {
        subtitle = `(${fmt(ws)}–${String(we.getUTCDate()).padStart(
          2,
          '0',
        )}, ${we.getUTCFullYear()})`;
      } else {
        subtitle = `(${fmt(ws)} – ${fmt(we, true)})`;
      }
    } else {
      subtitle = `(${fmt(ws, true)} – ${fmt(we, true)})`;
    }

    out.push({
      table: `WEEK ${weekNumber}`,
      subtitle,
      value: toISODateUTC(ws), // lunes ISO
    });

    // siguiente semana
    cursor = new Date(cursor);
    cursor.setUTCDate(cursor.getUTCDate() + 7);
  }

  return out;
};

// ---------- HOOK ----------
export function useUsageLast7DaysVanilla({
  unitId,
  showTransit = true,
  enabled = true,
  mode, // 'week' | 'month'
  dateSelected, // ancla (YYYY-MM-DD o Date). Opcional
}) {
  // ancla: si no viene, usamos hoy
  const anchorISO = useMemo(() => {
    if (!dateSelected) return toISODateUTC(asUtcMidnight(new Date()));
    if (dateSelected instanceof Date) return toISODateUTC(asUtcMidnight(dateSelected));
    return String(dateSelected).slice(0, 10);
  }, [dateSelected]);

  // items según modo y ancla
  const items = useMemo(() => {
    if (mode === 'week') {
      const monday = startOfWeekUTC(anchorISO, 0);
      return getSevenDaysFromMonday(toISODateUTC(monday)); // [{label,value}]
    }
    if (mode === 'month') {
      return getWeeksOfMonthFromAnchor(anchorISO, 0); // [{table,subtitle,value (monday)}]
    }
    return [];
  }, [mode, anchorISO]);

  const [data, setData] = useState([]); // week: [{label,date,metrics}] | month: [{table,subtitle,value,metrics,days:[]}]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const load = useCallback(
    async (overrides = {}) => {
      const effectiveUnit = overrides.unitId ?? unitId;
      const effectiveShowTransit = overrides.showTransit ?? showTransit;
      if (!effectiveUnit) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError(null);
      try {
        if (mode === 'week') {
          // 1 request por día
          const results = await Promise.allSettled(
            items.map((it) =>
              getFacilityMetricsSummary(
                effectiveUnit.esp32_mac_address,
                it.value,
                effectiveShowTransit,
                { signal: controller.signal },
              ),
            ),
          );

          const mapped = results.map((res, i) => ({
            label: items[i].label,
            date: items[i].value,
            metrics: res.status === 'fulfilled' ? res.value?.metrics ?? null : null,
            error: res.status === 'rejected' ? res.reason : null,
          }));

          if (!controller.signal.aborted) setData(mapped);
          return;
        }

        if (mode === 'month') {
          // Por cada semana: 1 weekly + 7 diarios
          const weekPromises = items.map(async (week) => {
            const weekDays = getSevenDaysFromMonday(week.value);

            const [weekSummaryRes, daysRes] = await Promise.allSettled([
              getFacilityMetricsSummary(
                effectiveUnit.esp32_mac_address,
                week.value, // lunes de la semana
                effectiveShowTransit,
                { signal: controller.signal },
              ),
              Promise.allSettled(
                weekDays.map((d) =>
                  getFacilityMetricsSummary(
                    effectiveUnit.esp32_mac_address,
                    d.value,
                    effectiveShowTransit,
                    { signal: controller.signal },
                  ),
                ),
              ),
            ]);

            const weekMetrics =
              weekSummaryRes.status === 'fulfilled' ? weekSummaryRes.value?.metrics ?? null : null;
            const weekError = weekSummaryRes.status === 'rejected' ? weekSummaryRes.reason : null;

            const daySettlements = daysRes.status === 'fulfilled' ? daysRes.value : [];
            const days = daySettlements.map((res, i) => ({
              label: weekDays[i].label,
              date: weekDays[i].value,
              metrics: res.status === 'fulfilled' ? res.value?.metrics ?? null : null,
              error: res.status === 'rejected' ? res.reason : null,
            }));

            return {
              table: week.table,
              subtitle: week.subtitle,
              value: week.value, // lunes
              metrics: weekMetrics,
              weekError,
              days,
            };
          });

          const weeks = await Promise.all(weekPromises);
          if (!controller.signal.aborted) setData(weeks);
          return;
        }
      } catch (e) {
        if (!controller.signal.aborted) setError(e);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [unitId, showTransit, items, mode],
  );

  // Auto-fetch solo si enabled === true
  useEffect(() => {
    if (!enabled) return;
    load();
    return () => abortRef.current?.abort();
  }, [enabled, load]);

  const reset = useCallback(() => setData([]), []);

  return { data, loading, error, refetch: load, reset };
}
